import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Badge, Box, Button, Heading, Spinner, Switch, Table, TextInput,
} from '@hyphen/hyphen-components';

import { api } from '../lib/api.js';
import { useResource } from '../lib/session.js';
// The app's one timestamp formatter. It lives next to the evidence views because
// that is where it was first needed, not because it is theirs.
import { When } from './evidence-shared.jsx';

const STATE_COLOR = { correct: 'green', drifted: 'yellow', absent: 'red' };

/**
 * What to do about a repository whose gate ruleset is absent and whose gate has
 * never reported. Shown on the row rather than only on the refusal, so the way
 * out is visible before the button is pressed.
 */
const NEVER_REPORTED =
  'The gate has never reported on this repository, so requiring its check would '
  + 'block every pull request there. Gate it, open or update a pull request, wait '
  + 'for the status to appear, then create the ruleset.';

/**
 * The merge gate: which repositories it runs on, how their live branch
 * protection compares with what the gate needs, and how it behaves.
 */
export function GateAdmin({ canEdit }) {
  // Two resources, deliberately. The database half answers immediately; the
  // GitHub half costs several round trips per gated repository, so the page
  // renders as soon as the first arrives rather than waiting on both — and a
  // GitHub failure leaves the settings readable instead of taking the page
  // down with it.
  const { data: config, error: configError, reload: reloadConfig } = useResource('/gate');
  // What GitHub said when it was last read, served at once, then a fresh read
  // swapped in when it arrives. Nothing waits on the fresh read: a change is
  // checked against GitHub by the server when it is made.
  const [repositories, setRepositories] = useState(null);
  const [repositoriesError, setRepositoriesError] = useState(null);
  const [refreshing, setRefreshing] = useState(true);
  const [refreshError, setRefreshError] = useState(null);

  // A load changes the page unless one started after it has already done so.
  // The fresh read takes seconds, and a repository gated meanwhile is reloaded
  // after it; were the read to land last, it would put back the row from before
  // the gating, showing it as never reported though the gate had just reported
  // on it. A load that fails leaves whatever was shown.
  const started = useRef(0);
  const shown = useRef(0);
  const load = useCallback((request) => {
    const id = ++started.current;
    return request().then((view) => {
      if (id > shown.current) {
        shown.current = id;
        setRepositories(view);
        // A failed last read is forgotten once a fresh one arrives.
        setRepositoriesError(null);
      }
      return view;
    });
  }, []);
  const loadRepositories = useCallback(
    () => load(() => api.get('/gate/repositories')),
    [load],
  );

  useEffect(() => {
    let cancelled = false;
    // The last read first, then the fresh one. The fresh one starts second, so
    // the last read can never land on top of it, but stays shown if it fails.
    loadRepositories().catch((err) => { if (!cancelled) setRepositoriesError(err.message); });
    load(() => api.post('/gate/repositories/refresh'))
      .catch((err) => { if (!cancelled) setRefreshError(err.message); })
      .finally(() => { if (!cancelled) setRefreshing(false); });
    return () => { cancelled = true; };
  }, [load, loadRepositories]);

  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  // What gating a repository did to the pull requests already open on it. Shown
  // because it is the proof the gate works there, which is what has to be true
  // before anybody requires its check.
  const [backfill, setBackfill] = useState(null);
  // Every repository by default. The page is where a repository gets gated in
  // the first place, and listing only the gated ones hid exactly the rows that
  // needed switching on.
  const [showAll, setShowAll] = useState(true);

  async function act(key, fn) {
    setBusy(key);
    setError(null);
    try {
      const result = await fn();
      await loadRepositories();
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setBusy(null);
    }
  }

  if (configError) return <Alert variant="danger">{configError}</Alert>;
  if (!config) return <Spinner />;

  return (
    <Box gap="4xl">
      <Heading as="h1" size="lg">Merge gate</Heading>
      {error && <Alert variant="warning">{error}</Alert>}
      {backfill && <Backfilled report={backfill} onDismiss={() => setBackfill(null)} />}

      <Repositories
        view={repositories}
        viewError={repositoriesError}
        refreshing={refreshing}
        refreshError={refreshError}
        canEdit={canEdit}
        busy={busy}
        showAll={showAll}
        onShowAll={setShowAll}
        onAct={act}
        onBackfill={setBackfill}
      />

      <Settings settings={config.settings} canEdit={canEdit} onSaved={reloadConfig} />
    </Box>
  );
}

/**
 * What gating a repository did to the pull requests already open on it.
 *
 * Worth its own alert rather than a line in the table: switching a repository on
 * is now the step that proves the gate reaches it, and "nineteen of nineteen
 * reported" is the answer the cutover used to ask a person to go and look for.
 */
function Backfilled({ report, onDismiss }) {
  const { repo, found, evaluated, skipped, failures = [] } = report;
  const failed = failures.length > 0;

  return (
    <Alert
      variant={failed ? 'warning' : 'success'}
      onClose={onDismiss}
      title={found === 0
        ? `No pull requests were open in ${repo}`
        : `Reported on ${evaluated} of ${found} open pull requests in ${repo}`}
    >
      <Box gap="xs">
        {found === 0 && (
          <Box as="span">
            Nothing to report on yet. Open a pull request and the gate will publish there.
          </Box>
        )}
        {skipped > 0 && (
          <Box as="span">
            {skipped} were past the limit this does in one go and were not evaluated. They
            will be reported on when something happens to them.
          </Box>
        )}
        {failures.map((failure) => (
          <Box key={failure} as="span" fontSize="sm">{failure}</Box>
        ))}
      </Box>
    </Alert>
  );
}

function Repositories({
  view, viewError, refreshing, refreshError, canEdit, busy, showAll, onShowAll, onAct, onBackfill,
}) {
  // Which row is asking "create anyway?". One at a time, because the question is
  // about the row it was asked on and a stale yes on another row would apply the
  // answer to the wrong repository.
  const [confirming, setConfirming] = useState(null);

  if (viewError) return <Alert variant="warning" title="Could not read GitHub">{viewError}</Alert>;
  // Only the very first read leaves nothing to show while it happens.
  if (!view || (!view.refreshed_at && refreshing)) return <Spinner />;
  if (!view.refreshed_at && refreshError) {
    return <Alert variant="warning" title="Could not read GitHub">{refreshError}</Alert>;
  }

  const all = view.repositories ?? [];
  const rows = showAll ? all : all.filter((r) => r.gated);

  const apply = (row, force) => onAct(row.full_name, async () => {
    setConfirming(null);
    const query = force ? '?force=true' : '';
    await api.post(`/gate/repositories/${row.owner}/${row.name}/fix${query}`);
  });

  const columns = [
    {
      heading: 'Repository',
      dataKey: 'full_name',
      render: (_cell, row) => (
        <Box direction="row" alignItems="center" gap="sm">
          <a href={`https://github.com/${row.full_name}`} target="_blank" rel="noopener">
            {row.full_name}
          </a>
          {row.archived && <Badge size="sm" variant="outline">archived</Badge>}
        </Box>
      ),
    },
    {
      heading: 'Gated',
      dataKey: 'gated',
      width: 90,
      render: (_cell, row) => (
        <Switch
          id={`gate-${row.full_name}`}
          label={`Gate ${row.full_name}`}
          hideLabel
          isChecked={row.gated}
          isDisabled={!canEdit || busy === row.full_name}
          onChange={async (event) => {
            const path = `/gate/repositories/${row.owner}/${row.name}`;
            if (!event.target.checked) {
              onBackfill(null);
              onAct(row.full_name, () => api.delete(path));
              return;
            }
            // Gating evaluates the pull requests already open, which is the
            // interesting part of having switched it on.
            const result = await onAct(row.full_name, () => api.put(path));
            onBackfill(result?.backfill ? { repo: row.full_name, ...result.backfill } : null);
          }}
        />
      ),
    },
    {
      heading: 'Protection',
      dataKey: 'state',
      render: (_cell, row) => <Protection repo={row} />,
    },
    {
      heading: '',
      dataKey: 'fix',
      width: 170,
      render: (_cell, row) => {
        if (!(row.gated && row.state && row.state !== 'correct' && canEdit)) return null;

        const creating = row.state === 'absent';
        const unreported = creating && !row.last_published_at;

        if (confirming === row.full_name) {
          return (
            <Box direction="row" gap="sm">
              <Button
                variant="danger"
                size="sm"
                isLoading={busy === row.full_name}
                onClick={() => apply(row, true)}
              >
                Create anyway
              </Button>
              <Button size="sm" variant="tertiary" onClick={() => setConfirming(null)}>
                Cancel
              </Button>
            </Box>
          );
        }

        return (
          <Button
            variant="secondary"
            size="sm"
            isDisabled={!view.can_write}
            isLoading={busy === row.full_name}
            title={view.can_write ? undefined : view.write_reason}
            onClick={() => (unreported ? setConfirming(row.full_name) : apply(row, false))}
          >
            {creating ? 'Create' : 'Fix'}
          </Button>
        );
      },
    },
  ];

  return (
    <Box gap="xl">
      <Box direction="row" alignItems="center" gap="xl">
        <Heading as="h2" size="md">Repositories</Heading>
        <Badge variant="outline">requires {view.check_name}</Badge>
        <Freshness at={view.refreshed_at} refreshing={refreshing} error={refreshError} />
        <Box style={{ marginLeft: 'auto' }}>
          <Switch
            id="show-ungated"
            label="Show ungated"
            isChecked={showAll}
            onChange={(event) => onShowAll(event.target.checked)}
          />
        </Box>
      </Box>

      {/* The order matters and nothing else on the page says so: gating starts
          the gate reporting, creating the ruleset makes its report required. */}
      <Box as="p" fontSize="sm" color="secondary" maxWidth="52rem" style={{ margin: 0 }}>
        Gating a repository starts the gate reporting on it, including on the pull requests
        already open. Creating its ruleset makes that report required. Do them in that order —
        Create waits until the gate has actually reported, because a required check that never
        arrives blocks every pull request.
      </Box>

      {!view.can_write && view.write_reason && (
        <Alert variant="default">{view.write_reason}</Alert>
      )}

      <Table rowKey="full_name" columns={columns} rows={rows} isScrollable={{ x: true }} />
    </Box>
  );
}

/**
 * When GitHub was last read, and whether it is being read again now. The rows
 * are what it said then, so how long ago that was belongs next to them.
 */
function Freshness({ at, refreshing, error }) {
  if (refreshing) {
    return (
      <Box direction="row" alignItems="center" gap="xs" fontSize="sm" color="secondary">
        <Spinner size="sm" /> Checking GitHub
      </Box>
    );
  }
  return (
    <Box as="span" fontSize="sm" color={error ? 'warning' : 'secondary'} title={error ?? undefined}>
      {error ? 'Could not check GitHub; read ' : 'Read from GitHub '}<When iso={at} />
    </Box>
  );
}

function Protection({ repo }) {
  if (!repo.gated) return <Box as="span" color="tertiary">—</Box>;
  if (repo.read_error) return <Box as="span" color="tertiary">{repo.read_error}</Box>;

  return (
    <Box gap="xs">
      <Box direction="row" alignItems="center" gap="sm">
        <Badge variant="soft" color={STATE_COLOR[repo.state] ?? 'grey'}>{repo.state ?? 'unknown'}</Badge>
        {/* Whether the gate has ever been heard from here, which is the fact that
            decides whether requiring its check gates the repository or closes it.
            Called out only where the ruleset is absent and that decision is still
            to be made: a repository already requiring the check is not waiting on
            this, and every repository gated before the app started recording it
            reads as unreported until its next publish. */}
        {repo.last_published_at && (
          <Box as="span" fontSize="sm" color="secondary">
            reported <When iso={repo.last_published_at} />
          </Box>
        )}
        {!repo.last_published_at && repo.state === 'absent' && (
          <Badge variant="outline" color="red">never reported</Badge>
        )}
      </Box>
      {!repo.last_published_at && repo.state === 'absent' && (
        <Box fontSize="sm" color="secondary" maxWidth="32rem">{NEVER_REPORTED}</Box>
      )}
      {(repo.findings ?? []).map((finding) => (
        <Box key={finding.summary} fontSize="sm">
          {finding.summary}
        </Box>
      ))}
    </Box>
  );
}

/**
 * The gate's settings, editable here.
 *
 * They were displayed and editable nowhere, which made the check name — the one
 * string that has to agree with every gated repository's ruleset — changeable
 * only by a migration. The design already says behaviour is edited on the site
 * rather than deployed; this is that.
 *
 * Save and Cancel rather than saving as you type: changing the check name
 * silently would stop every ruleset matching, and emptying the required
 * reviewers would make the gate pass everything while looking like it works.
 */
function Settings({ settings, canEdit, onSaved }) {
  const asText = (values) => (values ?? []).join(', ');
  const [draft, setDraft] = useState({
    check_name: settings.check_name,
    required_reviewers: asText(settings.required_reviewers),
    skip_markers: asText(settings.skip_markers),
    error_markers: asText(settings.error_markers),
  });
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(null);

  const stored = {
    check_name: settings.check_name,
    required_reviewers: asText(settings.required_reviewers),
    skip_markers: asText(settings.skip_markers),
    error_markers: asText(settings.error_markers),
  };
  const changed = Object.keys(stored).some((key) => draft[key] !== stored[key]);

  const fields = [
    {
      key: 'check_name',
      label: 'Check name',
      hint: 'The commit status every gated repository requires. Changing it means pressing Fix on each one, or their rulesets go on requiring a check that no longer reports.',
    },
    {
      key: 'required_reviewers',
      label: 'Required reviewers',
      hint: 'Comma separated. With none the gate passes everything, which looks like it is working.',
    },
    { key: 'skip_markers', label: 'Skip markers', hint: 'Comma separated.' },
    { key: 'error_markers', label: 'Error markers', hint: 'Comma separated.' },
  ];

  const list = (value) =>
    value.split(',').map((item) => item.trim()).filter(Boolean);

  async function save() {
    setSaving(true);
    setFailed(null);
    try {
      await api.put('/gate/settings', {
        check_name: draft.check_name.trim(),
        required_reviewers: list(draft.required_reviewers),
        skip_markers: list(draft.skip_markers),
        error_markers: list(draft.error_markers),
      });
      await onSaved();
    } catch (err) {
      setFailed(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit) {
    return (
      <Box gap="xl" maxWidth="48rem">
        <Heading as="h2" size="md">Settings</Heading>
        <Table
          rowKey="name"
          columns={[
            { heading: 'Setting', dataKey: 'name', width: 220 },
            {
              heading: 'Value',
              dataKey: 'value',
              render: (cell) => cell || <Box as="span" color="tertiary">—</Box>,
            },
          ]}
          rows={fields.map((field) => ({ name: field.label, value: stored[field.key] }))}
        />
      </Box>
    );
  }

  return (
    <Box gap="xl" maxWidth="48rem">
      <Heading as="h2" size="md">Settings</Heading>
      {failed && <Alert variant="warning">{failed}</Alert>}
      {fields.map((field) => (
        <Box key={field.key} gap="xs">
          <TextInput
            id={`gate-${field.key}`}
            label={field.label}
            value={draft[field.key]}
            onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
          />
          <Box as="span" fontSize="sm" color="secondary">{field.hint}</Box>
        </Box>
      ))}
      {changed && (
        <Box direction="row" gap="sm" alignItems="center">
          <Button size="sm" isLoading={saving} onClick={save}>Save</Button>
          <Button size="sm" variant="secondary" isDisabled={saving} onClick={() => setDraft(stored)}>
            Cancel
          </Button>
          <Box as="span" fontSize="sm" color="secondary">Unsaved changes</Box>
        </Box>
      )}
    </Box>
  );
}
